import { createRouter, createWebHistory, RouteMeta, RouteRecordRaw } from 'vue-router';
import CmsPageProxy from '@/core/content/CmsPageProxy.vue';
import bus from '@/core/bus';
import { NEW_PAGE_SET } from '@/core/content/constants';
import { nextTick, ref } from 'vue';
import { CheckoutStep } from '../api/checkout/models/checkout-step';
import { trackPage } from '@/project/tracking/tracking.service';
import { getCultureSettings } from '@/project/content/api/contentApi';
import { basketKeys } from '@/project/basket/basket.service';
import { getBasket } from '@/project/basket/basket.service';
import { sessionStorageService } from '@/core/storage/storage.service';
import { pageCache } from '@/core/content/cmsPageProxy';
import { getSelectedCountry, setSelectedCountry } from '@/project/pages/countrySelector.utils';
import { extractTwoLetterCultureFromCookie, extractTwoLetterCultureFromPath } from '@/project/intl/useCulture';

const giftCardEnabledPromise = getCultureSettings().then(settings => settings.isMarketGiftCardOnly);

export enum PageNames {
    GiftCardDetail = 'GiftCardDetails',
    GiftCardCheckoutInformation = 'GiftcardInformation',
    GiftCardCheckoutPayment = 'GiftCardPayment',
    GiftCardCheckoutReceipt = 'GiftCardReceipt',
}

export interface RouterMetaData extends RouteMeta {
    checkoutStep?: CheckoutStep,
    isServerPage?: boolean
}

export function getCurrentRouteMetaData() : RouterMetaData | undefined {
    return router.currentRoute.value?.meta as RouterMetaData | undefined;
}

export function getPageData() {
    return {
        meta: pageCache.get(router.currentRoute.value.path)?.metadata || router.currentRoute.value?.meta,
        alias: pageCache.get(router.currentRoute.value.path)?.alias || router.currentRoute.value?.name,
    };
}

// Setup dynamically
const routes: Array<RouteRecordRaw> = [];
export const rootPath = '/';

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        scrollToAnchor(to, savedPosition);
        if (!to.meta.isServerPage) {
            trackPage();
            return {
                top: savedPosition?.top ?? 0,
            };
        }
        return new Promise((resolve) => {
            bus.once(NEW_PAGE_SET, () => {
                // Wait for new page data to be set and wait a bit for rendering
                nextTick(() => {
                    resolve({
                        top: savedPosition?.top ?? 0,
                    });
                });
                if (to.meta.isServerPage) trackPage();
            });
        });
    },
});

// Pages and content are async, so try to look up anchor some time before scrolling
const timesToTry = 20;
let timesTried = 0;
function scrollToAnchor(to, savedPosition) {
    if (to.hash && !savedPosition) {
        const anchor = document.querySelector(to.hash);
        if (anchor) {
            const headerOffset = 65;
            const elementPosition = anchor.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
        else {
            setTimeout(()=> {
                timesTried += 1;
                if (timesTried === timesToTry) return;
                scrollToAnchor(to, savedPosition);
            }, 300);
        }
    }
}

let routerInitialized = false;
router.beforeEach((to, from)=> {
    const urlCulture = extractTwoLetterCultureFromPath(to.path);
    if (urlCulture) {
        const cookieValue = getSelectedCountry();
        const cookieCulture = extractTwoLetterCultureFromCookie(cookieValue);

        // Keep cookie in sync with the URL and normalize legacy values like "/fr" -> "fr".
        if (cookieCulture !== urlCulture || cookieValue !== urlCulture) {
            setSelectedCountry(urlCulture);
        }
    }

    if (routerInitialized && to.path === from.path) {
        scrollToAnchor(to, null);
        return false;
    }
});

const lastPathBeforeCheckout = ref(rootPath);
router.afterEach((to, from) => {
    if (!(to.meta as RouterMetaData).checkoutStep) {
        // Remember last step outside checkout to be able to go back from checkout
        lastPathBeforeCheckout.value = to.fullPath;
    }
    if ((from.meta as RouterMetaData).checkoutStep && !(to.meta as RouterMetaData).checkoutStep) {
        sessionStorageService.removeItem(basketKeys.GiftCard);
        setTimeout(()=> {
            getBasket();
        });
    }
});

export function getLastPathBeforeCheckout(): string {
    return lastPathBeforeCheckout.value;
}

export async function setupDynamicRoutes({ giftCardCheckoutInformationUrl, giftCardCheckoutPaymentUrl, giftCardCheckoutReceiptUrl }: {
    giftCardCheckoutInformationUrl: string,
    giftCardCheckoutPaymentUrl: string,
    giftCardCheckoutReceiptUrl: string,
}): Promise<void> {
    const giftCardEnabled = await giftCardEnabledPromise;
    if(giftCardEnabled) {
        router.addRoute({
            name: PageNames.GiftCardCheckoutReceipt,
            path: giftCardCheckoutReceiptUrl,
            meta: { checkoutStep: CheckoutStep.Receipt },
            component: () => import('@/project/pages/checkout/giftcard/GiftCardCheckoutReceipt.vue'),
        });

        router.addRoute({
            path: `/${getBasePath(giftCardCheckoutInformationUrl)}`,
            component: () => import('@/project/pages/checkout/giftcard/GiftCardCheckoutView.vue'),
            children: [
                {
                    name: PageNames.GiftCardCheckoutInformation,
                    path: `${getLeafPath(giftCardCheckoutInformationUrl)}`,
                    meta: { checkoutStep: CheckoutStep.Information },
                    component: () => import('@/project/pages/checkout/giftcard/GiftCardCheckoutInformation.vue'),
                },
                {
                    name: PageNames.GiftCardCheckoutPayment,
                    path: `${getLeafPath(giftCardCheckoutPaymentUrl)}`,
                    meta: { checkoutStep: CheckoutStep.Payment },
                    component: () => import('@/project/pages/checkout/giftcard/GiftCardCheckoutPayment.vue'),
                },
            ],
        });

        router.addRoute({
            name: PageNames.GiftCardDetail,
            path: '/gift-card-pdp/',
            component: () => import('@/project/pages/gift-card-details/GiftCardDetailsPage.vue'),
        });
    }
    // Fallback for CMS
    router.addRoute({
        path: '/:pathMatch(.*)*',
        component: CmsPageProxy,
        meta: {
            isServerPage: true,
        },
        beforeEnter: ()=> {
            routerInitialized = true;
        },
    });

    function getBasePath(path: string): string {
        const elems = path.split('/').filter(e => e); // Remove empty strings
        if (elems.length < 2) throw new Error('Path needs at least base and subpath');
        return elems.length >= 3 ? `${elems[0]}/${elems[1]}` : `${elems[0]}`;
    }

    function getLeafPath(path: string): string {
        const elems = path.split('/').filter(e => e); // Remove empty strings
        if (elems.length < 2) throw new Error('Path needs at least base and subpath');
        return `${elems[elems.length - 1]}`;
    }
}

export default router;
